import { PipeTransform, Pipe } from '@angular/core';
import { SelectionItem } from '../component/select/select.component';
import { getTranslation } from '../helper/translation';
import { Money } from '../model/money';
import { LanguageService } from '../service/language.service';

const CURRENCY_SEK = ' kr';
const CURRENCY_DKK = ' dkr';
const CURRENCY_EUR = ' €';
const SEPARATOR = ',';

function shiftDecimal(value: number, places: number): number {
  const [coefficient, exponent = '0'] = value.toString().split('e');
  return Number(`${coefficient}e${Number(exponent) + places}`);
}

function roundToDecimals(value: number, decimalPlaces: number): number {
  return shiftDecimal(Math.round(shiftDecimal(value, decimalPlaces)), -decimalPlaces);
}

export class Currencies {
  static SEK = 'SEK';
  static DKK = 'DKK';
  static EUR = 'EUR';

  static getDefaultCurrency(): string {
    switch (LanguageService.getCultureLang()) {
      case 'da-DK':
        return Currencies.DKK;
      case 'fi-FI':
        return Currencies.EUR;
      default:
        return Currencies.SEK;
    }
  }
}

export const CurrencySelection = [
  new SelectionItem(Currencies.SEK, Currencies.SEK),
  new SelectionItem(Currencies.EUR, Currencies.EUR),
  new SelectionItem(Currencies.DKK, Currencies.DKK),
];

@Pipe({
    name: 'money',
    standalone: false
})
export class CurrencyPipe implements PipeTransform {
  constructor(
    private readonly _languageService: LanguageService) {}

  private _getDefault(): number {
    return 0;
  }

  private getCurrency(code: string) {
    switch (code) {
      case Currencies.DKK:
        return CURRENCY_DKK;
      case Currencies.EUR:
        return CURRENCY_EUR;
      case Currencies.SEK:
        return CURRENCY_SEK;
      default:
        return '';
    }
  }
  transform(value: any, hideDecimals?: boolean, suffix?: string, currencyCode?: string) {
    if (value === undefined || value === null) {
      return null;
    }
    if (typeof value === 'string') {
      value = value.replace(SEPARATOR, '.');
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }
    const amount = roundToDecimals(numericValue, 2);
    let result = amount
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$& ')
      .replace('.', SEPARATOR);
    if (hideDecimals) {
      result = result.substr(0, result.length - 3);
    }
    var currency = this.getCurrency(currencyCode);
    return result + currency + this.getSuffix(suffix);
  }

  private getSuffix(suffix) {
    return suffix ? '/' + getTranslation(this._languageService.getCurrentTranslations(), suffix) : '';
  }

  toNumber(value: any, currencyCode: string, suffix?: string): number {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      value = value
        .replace(' ', '')
        .replace(SEPARATOR, '.')
        .replace(this.getCurrency(currencyCode), '')
        .replace(this.getSuffix(suffix), '');
    } else {
      value = this._getDefault();
    }
    let result = Number.parseFloat(value);
    return Number.isNaN(result) ? null : result;
  }

  toMoney(value: any, currencyCode: string, suffix?: string): Money {
    return new Money(currencyCode, this.toNumber(value, currencyCode, suffix));
  }
}
